import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaUserPlus, FaBuilding, FaSearch, FaUserShield, FaDownload, FaCheckCircle } from 'react-icons/fa';

interface VisitorItem {
  user_id: number;
  name: string;
  email: string;
  number: string;
  company_name: string;
  designation: string;
  business_classification: string;
  role: string;
  status: string;
  chapter_name: string;
  created_at: string;
}

const VisitorListReport: React.FC = () => {
  const [visitors, setVisitors] = useState<VisitorItem[]>([]);
  const [chapters, setChapters] = useState<any[]>([]);
  const [selectedChapterId, setSelectedChapterId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  // Conversion Modal State
  const [convertModalVisitor, setConvertModalVisitor] = useState<VisitorItem | null>(null);
  const [convertForm, setConvertForm] = useState({
    society_year: '2025-2026',
    sub_role: 'Member',
    designation: 'Member',
    business_classification: ''
  });
  const [convertLoading, setConvertLoading] = useState(false);
  const [convertMsg, setConvertMsg] = useState('');

  useEffect(() => {
    fetchChapters();
    fetchReport('');
  }, []);

  const fetchChapters = async () => {
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/chapters');
      if (res.data.success) {
        setChapters(res.data.chapters || []);
      }
    } catch (err) {
      console.error('Error fetching chapters:', err);
    }
  };

  const fetchReport = async (chapId: string) => {
    try {
      setLoading(true);
      const url = chapId
        ? `https://rmbbackend.rmbjaipur.com/auth/reports/visitors?chapter_id=${chapId}`
        : 'https://rmbbackend.rmbjaipur.com/auth/reports/visitors';
      const res = await axios.get(url);
      if (res.data.success) {
        setVisitors(res.data.visitors || []);
      }
    } catch (err) {
      console.error('Error fetching visitors report:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChapterChange = (chapId: string) => {
    setSelectedChapterId(chapId);
    fetchReport(chapId);
  };

  const handleConvertSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!convertModalVisitor) return;
    setConvertLoading(true);
    setConvertMsg('');

    try {
      const res = await axios.put(`https://rmbbackend.rmbjaipur.com/auth/admin/convert-visitor-to-member/${convertModalVisitor.user_id}`, convertForm);
      if (res.data.success) {
        setConvertMsg('✨ Visitor successfully converted to Member!');
        setTimeout(() => {
          setConvertModalVisitor(null);
          setConvertMsg('');
          fetchReport(selectedChapterId);
        }, 1500);
      }
    } catch (err) {
      console.error('Error converting visitor:', err);
      setConvertMsg('❌ Failed to convert visitor');
    } finally {
      setConvertLoading(false);
    }
  };

  const filteredVisitors = visitors.filter(v => {
    const q = searchQuery.toLowerCase();
    return (
      (v.name || '').toLowerCase().includes(q) ||
      (v.company_name || '').toLowerCase().includes(q) ||
      (v.email || '').toLowerCase().includes(q) ||
      (v.chapter_name || '').toLowerCase().includes(q)
    );
  });

  const exportCSV = () => {
    const headers = ['Visitor Name', 'Company Name', 'Classification', 'Chapter', 'Mobile', 'Email', 'Role / Status', 'Joined Date'];
    const rows = filteredVisitors.map(v => [
      `"${v.name || ''}"`,
      `"${v.company_name || ''}"`,
      `"${v.business_classification || v.designation || ''}"`,
      `"${v.chapter_name || ''}"`,
      `"${v.number || ''}"`,
      `"${v.email || ''}"`,
      `"${v.role || ''}"`,
      `"${v.created_at ? new Date(v.created_at).toLocaleDateString() : ''}"`
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Chapter_Visitors_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">

      {/* Header Banner */}
      <div className="bg-[#0A2451] rounded-2xl p-6 text-white shadow-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-black flex items-center gap-3 text-[#FDBC12]">
            <FaUserShield /> Chapter Visitors Report & Member Conversion
          </h2>
          <p className="text-xs text-slate-300 mt-1">
            View all event visitors chapter-wise, manage guest accounts, and convert interested visitors into full Active Members with 1 click.
          </p>
        </div>

        <button
          onClick={exportCSV}
          disabled={filteredVisitors.length === 0}
          className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold text-xs rounded-xl shadow-lg flex items-center gap-2 transition cursor-pointer"
        >
          <FaDownload /> Export Visitors CSV
        </button>
      </div>

      {/* Filters Bar */}
      <div className="flex flex-col sm:flex-row justify-between gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex items-center gap-2">
          <FaBuilding className="text-slate-400 text-xs" />
          <span className="text-xs font-bold text-slate-700 uppercase">Chapter Filter:</span>
          <select
            value={selectedChapterId}
            onChange={(e) => handleChapterChange(e.target.value)}
            className="p-2 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 bg-slate-50 focus:ring-2 focus:ring-amber-400 outline-none cursor-pointer"
          >
            <option value="">All Whitelabel Chapters</option>
            {chapters.map(ch => (
              <option key={ch.id} value={ch.id}>
                {ch.name} ({ch.code})
              </option>
            ))}
          </select>
        </div>

        <div className="relative w-full sm:w-72">
          <FaSearch className="absolute left-3 top-3 text-slate-400 text-xs" />
          <input
            type="text"
            placeholder="Search visitor name or company..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-4 py-2 border border-slate-300 rounded-xl text-xs font-medium focus:ring-2 focus:ring-amber-400 outline-none"
          />
        </div>
      </div>

      {/* Visitors Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead className="bg-slate-900 text-slate-200 uppercase text-[10px] tracking-wider font-bold">
              <tr>
                <th className="p-3.5">Visitor Name</th>
                <th className="p-3.5">Chapter</th>
                <th className="p-3.5">Company & Classification</th>
                <th className="p-3.5">Registered Date</th>
                <th className="p-3.5 text-center">Status / Role</th>
                <th className="p-3.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {loading ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-slate-400 font-medium">Loading visitor records...</td>
                </tr>
              ) : filteredVisitors.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-slate-400 font-medium">No visitor records found.</td>
                </tr>
              ) : (
                filteredVisitors.map((v) => (
                  <tr key={v.user_id} className="hover:bg-slate-50 transition">
                    <td className="p-3.5 font-bold text-slate-900">
                      <div>{v.name}</div>
                      <div className="text-[10px] text-slate-400 font-normal">{v.email} • {v.number}</div>
                    </td>
                    <td className="p-3.5 font-semibold text-blue-700">{v.chapter_name}</td>
                    <td className="p-3.5">
                      <div className="font-bold text-slate-800">{v.company_name || 'Guest Visitor'}</div>
                      <div className="text-[10px] text-amber-600 font-semibold">{v.business_classification || v.designation || 'Visitor'}</div>
                    </td>
                    <td className="p-3.5 text-slate-500 font-medium">
                      {v.created_at ? new Date(v.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="p-3.5 text-center">
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase ${v.role === 'Member' ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : 'bg-amber-100 text-amber-900 border border-amber-300'}`}>
                        {v.role === 'Member' ? 'Active Member' : 'Guest Visitor'}
                      </span>
                    </td>
                    <td className="p-3.5 text-right">
                      {v.role === 'visitor' ? (
                        <button
                          onClick={() => {
                            setConvertModalVisitor(v);
                            setConvertForm({
                              society_year: '2025-2026',
                              sub_role: 'Member',
                              designation: v.designation || 'Member',
                              business_classification: v.business_classification || ''
                            });
                          }}
                          className="px-3 py-1.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-950 font-black text-xs rounded-xl shadow transition cursor-pointer flex items-center gap-1.5 ml-auto"
                        >
                          <FaUserPlus /> Convert to Member
                        </button>
                      ) : (
                        <span className="text-[11px] font-bold text-emerald-600 flex items-center gap-1 justify-end">
                          <FaCheckCircle /> Converted Member
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* CONVERT VISITOR TO MEMBER MODAL */}
      {convertModalVisitor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-white border border-slate-200 rounded-2xl max-w-md w-full p-6 text-slate-900 shadow-2xl space-y-4">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
                <FaUserPlus className="text-amber-500" /> Convert Visitor to Member
              </h3>
              <button onClick={() => setConvertModalVisitor(null)} className="text-slate-400 hover:text-slate-700 font-bold">
                ✕
              </button>
            </div>

            <p className="text-xs text-slate-500">
              Upgrade guest <strong className="text-slate-900">{convertModalVisitor.name}</strong> ({convertModalVisitor.email}) to full Active Member role.
            </p>

            <form onSubmit={handleConvertSubmit} className="space-y-3 text-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Society Year</label>
                <input
                  type="text"
                  value={convertForm.society_year}
                  onChange={e => setConvertForm({ ...convertForm, society_year: e.target.value })}
                  className="w-full p-2.5 border border-slate-300 rounded-xl font-medium"
                  required
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Sub Role</label>
                <input
                  type="text"
                  value={convertForm.sub_role}
                  onChange={e => setConvertForm({ ...convertForm, sub_role: e.target.value })}
                  className="w-full p-2.5 border border-slate-300 rounded-xl font-medium"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Business Classification / Category</label>
                <input
                  type="text"
                  placeholder="e.g. IT & Software Development"
                  value={convertForm.business_classification}
                  onChange={e => setConvertForm({ ...convertForm, business_classification: e.target.value })}
                  className="w-full p-2.5 border border-slate-300 rounded-xl font-medium"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Designation</label>
                <input
                  type="text"
                  placeholder="e.g. Founder & CEO"
                  value={convertForm.designation}
                  onChange={e => setConvertForm({ ...convertForm, designation: e.target.value })}
                  className="w-full p-2.5 border border-slate-300 rounded-xl font-medium"
                />
              </div>

              {convertMsg && (
                <div className={`p-3 rounded-xl font-bold ${convertMsg.startsWith('✨') ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                  {convertMsg}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setConvertModalVisitor(null)}
                  className="w-1/2 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={convertLoading}
                  className="w-1/2 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 disabled:opacity-50 text-slate-950 font-black rounded-xl shadow transition cursor-pointer"
                >
                  {convertLoading ? 'Converting...' : 'Confirm Conversion'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default VisitorListReport;
